import {Node} from 'slate';

// ---------------------------------------------------------------------------
// Dynamic height estimation
// Estimates are derived from node properties, not hardcoded constants.
// The measurements map (from real DOM) always takes priority.
// ---------------------------------------------------------------------------

/**
 * Resolves the effective font size for a node.
 * Reads node.fontSize if present, otherwise falls back to type-based defaults.
 */
const resolveNodeFontSize = (node: any): number => {
  if (node.fontSize && Number.isFinite(Number(node.fontSize))) {
    return Number(node.fontSize);
  }
  switch (node.type) {
    case 'heading-one':
      return 28;
    case 'heading-two':
      return 20;
    case 'heading-three':
      return 16;
    default:
      return 12; // matches editor CSS base
  }
};

/**
 * Resolves the effective line height multiplier for a node.
 */
const resolveLineHeight = (node: any): number =>
  Math.max(1, Number(node.lineHeight ?? 1.6));

/**
 * Estimates the vertical margin (top + bottom) for a node type.
 * These mirror the actual CSS margins in the editor stylesheet.
 */
const resolveNodeMargin = (node: any): number => {
  switch (node.type) {
    case 'heading-one':
      return 28; // margin: 16px top + 12px bottom
    case 'heading-two':
      return 24; // margin: 14px top + 10px bottom
    case 'block-quote':
      return 24; // margin: 12px top + 12px bottom
    case 'paragraph':
      return 4; // margin-bottom: 4px
    case 'list-item':
      return 0;
    case 'bulleted-list':
    case 'numbered-list':
      return 6; // 0.25em top + bottom at 12px
    case 'table':
      return 16; // margin-bottom: 1em ≈ 16px
    default:
      return 0;
  }
};

/**
 * Estimates how many lines a text node will wrap to given a width constraint.
 * Uses average character width of 0.45× font size — empirically closer to
 * real browser rendering for mixed Latin text at typical document font sizes.
 */
const estimateLineCount = (
  text: string,
  fontSize: number,
  widthPx: number,
  indent = 0
): number => {
  const usableWidth = Math.max(1, widthPx - indent);
  // 0.45 is a better average for proportional fonts (was 0.55 which was too wide)
  const avgCharWidth = fontSize * 0.45;
  const charsPerLine = Math.max(1, Math.floor(usableWidth / avgCharWidth));

  return Math.max(1, Math.ceil((text.length || 1) / charsPerLine));
};

/**
 * Core height estimator. Always checks the measurements map first.
 * Falls back to dynamic calculation based on node properties.
 */
const estimateHeight = (
  node: any,
  widthConstraint: number,
  measurements: any = null
): number => {
  // 1. Real DOM measurement takes absolute priority
  if (measurements && typeof measurements.get === 'function') {
    const measured = measurements.get(node);
    if (Number.isFinite(measured) && measured > 0) return measured;
  }

  // 2. Explicit height property (images, shapes, framework-question, etc.)
  if (node.height && Number.isFinite(Number(node.height))) {
    return Number(node.height) + resolveNodeMargin(node);
  }

  // 3. Structural / container nodes — recurse into children
  switch (node.type) {
    case 'page-break':
      return 0; // Invisible in flow

    case 'spacer': {
      if (node.size === 'sm') return 8;
      if (node.size === 'md') return 16;
      if (node.size === 'lg') return 32;
      if (node.size === 'xl') return 64;

      return 16;
    }

    case 'heading-one':
    case 'heading-two':
    case 'heading-three': {
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const text = Node.string(node);
      const lines = estimateLineCount(text, fs, widthConstraint);

      return lines * fs * lh + resolveNodeMargin(node);
    }

    case 'block-quote': {
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const text = Node.string(node);
      // 12px left padding for the border
      const lines = estimateLineCount(text, fs, widthConstraint, 12);

      return lines * fs * lh + resolveNodeMargin(node);
    }

    case 'paragraph': {
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const text = Node.string(node);
      const lines = estimateLineCount(text, fs, widthConstraint);

      return lines * fs * lh + resolveNodeMargin(node);
    }

    case 'list-item': {
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const text = Node.string(node);
      const indentLevel = Math.max(0, Number(node.indent || 0));
      const totalIndent = indentLevel * fs * 2;
      const lines = estimateLineCount(text, fs, widthConstraint, totalIndent);

      return lines * fs * lh + resolveNodeMargin(node);
    }

    case 'bulleted-list':
    case 'numbered-list': {
      if (!Array.isArray(node.children) || node.children.length === 0)
        return 24;
      const itemsHeight = node.children.reduce(
        (sum: number, item: any) =>
          sum + estimateHeight(item, widthConstraint, measurements),
        0
      );

      return itemsHeight + resolveNodeMargin(node);
    }

    case 'table': {
      if (!Array.isArray(node.children) || node.children.length === 0)
        return 40;
      const rowsHeight = node.children.reduce(
        (sum: number, row: any) =>
          sum + estimateHeight(row, widthConstraint, measurements),
        0
      );

      return rowsHeight + resolveNodeMargin(node);
    }

    case 'table-row': {
      if (!Array.isArray(node.children) || node.children.length === 0)
        return 36;
      // Row height = tallest cell
      const cellHeights = node.children.map((cell: any) =>
        estimateHeight(
          cell,
          widthConstraint / Math.max(1, node.children.length),
          measurements
        )
      );

      return Math.max(...cellHeights, 36);
    }

    case 'table-cell': {
      const text = Node.string(node);
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const cellWidth = widthConstraint;
      const lines = estimateLineCount(text, fs, cellWidth);

      return lines * fs * lh + 16; // 8px padding top + bottom
    }

    case 'columns-container': {
      if (!Array.isArray(node.children) || node.children.length === 0)
        return 80;
      const colCount = Math.max(1, node.children.length);
      const gap = Number(node.gap ?? 24);
      const colWidth = Math.max(
        1,
        (widthConstraint - gap * (colCount - 1)) / colCount
      );
      const colHeights = node.children.map((col: any) =>
        estimateHeight(col, colWidth, measurements)
      );

      return Math.max(...colHeights, 40) + 16; // 16px container overhead
    }

    case 'column': {
      if (!Array.isArray(node.children) || node.children.length === 0)
        return 40;

      return node.children.reduce(
        (sum: number, child: any) =>
          sum + estimateHeight(child, widthConstraint, measurements),
        0
      );
    }

    case 'header-wrapper':
    case 'footer-wrapper': {
      if (!Array.isArray(node.children) || node.children.length === 0) return 0;
      const innerH = node.children.reduce(
        (sum: number, c: any) =>
          sum + estimateHeight(c, widthConstraint, measurements),
        0
      );

      return innerH + 20; // padding
    }

    case 'link': {
      // Inline — treat like a paragraph
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);
      const text = Node.string(node);
      const lines = estimateLineCount(text, fs, widthConstraint);

      return lines * fs * lh + resolveNodeMargin(node);
    }

    default: {
      // Unknown node type: if it has children, sum them; otherwise one line
      if (Array.isArray(node.children) && node.children.length > 0) {
        return node.children.reduce(
          (sum: number, child: any) =>
            sum + estimateHeight(child, widthConstraint, measurements),
          0
        );
      }
      // Single-line fallback using node's own font properties
      const fs = resolveNodeFontSize(node);
      const lh = resolveLineHeight(node);

      return fs * lh + resolveNodeMargin(node);
    }
  }
};

// ---------------------------------------------------------------------------
// Node utilities
// ---------------------------------------------------------------------------

const sanitizeNode = (node: any): any => {
  if (!node || typeof node !== 'object') return node;

  if (node.type === 'column') {
    const kids = Array.isArray(node.children) ? node.children : [];
    const cleaned = kids
      .filter(
        (c: any) =>
          c && c.type !== 'header-wrapper' && c.type !== 'footer-wrapper'
      )
      .map(sanitizeNode);

    return {
      ...node,
      children:
        cleaned.length > 0
          ? cleaned
          : [{type: 'paragraph', children: [{text: ''}]}]
    };
  }

  if (node.type === 'columns-container') {
    const cols = Array.isArray(node.children) ? node.children : [];
    const cleaned = cols.map(sanitizeNode).filter(Boolean);

    return {
      ...node,
      children:
        cleaned.length > 0
          ? cleaned
          : [
              {
                type: 'column',
                children: [{type: 'paragraph', children: [{text: ''}]}]
              }
            ]
    };
  }

  return node;
};

// No generic cloneNode — use spread or specialized cloning to preserve identities
// const cloneNode = (node: any) => JSON.parse(JSON.stringify(node));

// ---------------------------------------------------------------------------
// Splitting logic
// ---------------------------------------------------------------------------

const splitColumnsContainerNode = (
  node: any,
  availableHeight: number,
  widthConstraint: number,
  measurements: any = null
): {fitNode: any; overflowNode: any | null} | null => {
  if (!node || node.type !== 'columns-container') return null;
  if (!Array.isArray(node.children) || node.children.length === 0) return null;

  const colCount = Math.max(1, node.children.length);
  const gap = Number(node.gap ?? 24);
  const colWidth = Math.max(
    1,
    (widthConstraint - gap * (colCount - 1)) / colCount
  );
  const targetHeight = Math.max(24, availableHeight - 16);

  const fitColumns: any[] = [];
  const overflowColumns: any[] = [];
  const overflowFlags: boolean[] = [];
  let hasFit = false;
  let hasOverflow = false;

  for (const col of node.children) {
    const kids = Array.isArray(col?.children) ? col.children : [];
    const fitKids: any[] = [];
    const overflowKids: any[] = [];
    let used = 0;

    for (const child of kids) {
      const h = estimateHeight(child, colWidth, measurements);
      if (used + h <= targetHeight || fitKids.length === 0) {
        fitKids.push(child);
        used += h;
      } else {
        overflowKids.push(child);
      }
    }

    if (fitKids.length > 0) hasFit = true;
    if (overflowKids.length > 0) hasOverflow = true;
    overflowFlags.push(overflowKids.length > 0);

    fitColumns.push({
      ...col,
      children:
        fitKids.length > 0
          ? fitKids
          : [{type: 'paragraph', children: [{text: ''}]}]
    });
    overflowColumns.push({
      ...col,
      children:
        overflowKids.length > 0
          ? overflowKids
          : [{type: 'paragraph', children: [{text: ''}]}]
    });
  }

  if (!hasFit) return null;

  // Pack overflow columns left-first
  const packed = [
    ...overflowColumns.filter((_, i) => overflowFlags[i]),
    ...overflowColumns.filter((_, i) => !overflowFlags[i])
  ];

  return {
    fitNode: {...node, children: fitColumns},
    overflowNode: hasOverflow ? {...node, children: packed} : null
  };
};

const splitTableNode = (
  node: any,
  availableHeight: number,
  widthConstraint: number,
  measurements: any = null
): {fitNode: any; overflowNode: any | null} | null => {
  if (!node || node.type !== 'table') return null;
  if (!Array.isArray(node.children) || node.children.length === 0) return null;

  const headerRow = node.children[0]; // Refrence, NOT clone (unless we really need to copy it)
  const headerH = estimateHeight(headerRow, widthConstraint, measurements);

  // NOTE: When splitting a table, we REPLICATE the header on the new page.
  // This is the only place where we MUST copy node identities.
  const createRowCopy = (r: any) => JSON.parse(JSON.stringify(r));

  const fitRows: any[] = [node.children[0]]; // Original ref
  const overflowRows: any[] = [];
  let used = headerH;

  for (let i = 1; i < node.children.length; i++) {
    const row = node.children[i];
    const rowH = estimateHeight(row, widthConstraint, measurements);
    if (used + rowH <= availableHeight || fitRows.length === 1) {
      fitRows.push(row); // Original ref
      used += rowH;
    } else {
      overflowRows.push(row); // Original ref
    }
  }

  if (overflowRows.length === 0) return null;

  return {
    fitNode: {...node, children: fitRows},
    overflowNode: {
      ...node,
      children: [createRowCopy(headerRow), ...overflowRows]
    }
  };
};

// ---------------------------------------------------------------------------
// Page layout helpers
// ---------------------------------------------------------------------------

function createPageLayout(
  pageNumber: number,
  nodes: any[],
  height: number
): {
  id: string;
  pageNumber: number;
  nodes: any[];
  height: number;
  isDirty: boolean;
} {
  return {
    id: `page-${pageNumber}`,
    pageNumber,
    nodes,
    height,
    isDirty: false
  };
}

export const createPage = (
  children: any[] = [],
  pageNumber?: number
): {type: 'page'; children: any[]; id: string} => ({
  type: 'page',
  children,
  id: pageNumber
    ? `page-${pageNumber}`
    : `temp-page-${Math.random().toString(36).substring(2, 9)}`
});

// ---------------------------------------------------------------------------
// Main layout function
// ---------------------------------------------------------------------------

/**
 * Distributes flat Slate nodes across pages.
 *
 * @param nodes       - Flat array of Slate nodes (already extracted from pages)
 * @param measurements - Hash-keyed measurement proxy { get(node), set(node, h) }
 * @param pageDimensions    - { width, height } in pixels
 * @param pageMargins - { top, right, bottom, left } in pixels
 * @param activeHeader - Header wrapper node or null
 * @param activeFooter - Footer wrapper node or null
 */
export const layoutDocument = (
  nodes: any[],
  measurements: any = {},
  pageDimensions: any = {height: 1123, width: 794},
  pageMargins: any = {top: 96, bottom: 96, left: 96, right: 96},
  activeHeader: any = null,
  activeFooter: any = null
): Array<{
  id: string;
  pageNumber: number;
  nodes: any[];
  height: number;
  isDirty: boolean;
}> => {
  const contentH = pageDimensions.height - pageMargins.top - pageMargins.bottom;
  const contentW = pageDimensions.width - pageMargins.left - pageMargins.right;

  // --- Header / footer templates ---
  const headerTpl = activeHeader;
  const footerTpl = activeFooter;

  const flatNodes: any[] = [];
  nodes.forEach((n) => {
    const children = n.type === 'page' ? n.children : [n];
    for (const node of children) {
      if (node.type === 'header-wrapper' || node.type === 'footer-wrapper') {
        continue;
      }

      const sanitized = sanitizeNode(node);
      if (
        measurements &&
        typeof measurements.get === 'function' &&
        typeof measurements.set === 'function' &&
        sanitized !== node
      ) {
        const h = measurements.get(node);
        if (Number.isFinite(h) && h > 0) measurements.set(sanitized, h);
      }
      flatNodes.push(sanitized);
    }
  });

  if (flatNodes.length === 0) {
    flatNodes.push({type: 'paragraph', children: [{text: ''}]});
  }

  // --- Header height cost ---
  let initialHeight = 0;
  if (headerTpl) {
    const hH = estimateHeight(headerTpl, contentW, measurements);
    const overflow = Math.max(0, hH - pageMargins.top - 10);
    initialHeight = overflow > 0 ? overflow : 0;
  }
  const effectiveContentH = contentH;
  const pages: any[] = [];
  const clone = (n: any) => (n ? JSON.parse(JSON.stringify(n)) : null);
  let currentChildren: any[] = headerTpl ? [clone(headerTpl)] : [];
  let currentH = initialHeight;

  const flushPage = () => {
    const hasFocusableNode = currentChildren.some(
      (n: any) =>
        !['header-wrapper', 'footer-wrapper', 'page-break'].includes(n.type)
    );

    if (!hasFocusableNode) {
      currentChildren.push({type: 'paragraph', children: [{text: ''}]});
    }

    if (footerTpl) currentChildren.push(clone(footerTpl));

    if (currentChildren.length > 0) {
      pages.push(
        createPageLayout(
          pages.length + 1,
          currentChildren,
          pageDimensions.height
        )
      );
    } else if (pages.length === 0) {
      const content: any[] = [{type: 'paragraph', children: [{text: ''}]}];
      if (headerTpl) content.unshift(clone(headerTpl));
      if (footerTpl) content.push(clone(footerTpl));
      pages.push(createPageLayout(1, content, pageDimensions.height));
    }
    currentChildren = headerTpl ? [clone(headerTpl)] : [];
    currentH = initialHeight;
  };

  for (let i = 0; i < flatNodes.length; i++) {
    const node = flatNodes[i];

    if (node.type === 'page-break') {
      currentChildren.push(node);
      flushPage();
      continue;
    }

    const nodeH = estimateHeight(node, contentW, measurements);

    if (currentH + nodeH > effectiveContentH) {
      const available = Math.max(0, effectiveContentH - currentH);

      // Try splitting containers / tables
      if (node.type === 'columns-container') {
        const split = splitColumnsContainerNode(
          node,
          available,
          contentW,
          measurements
        );
        if (split?.fitNode) {
          const fitH = estimateHeight(split.fitNode, contentW, measurements);
          if (fitH <= available + 2) {
            currentChildren.push(split.fitNode);
            currentH += fitH;
            if (split.overflowNode) {
              flushPage();
              flatNodes.splice(i + 1, 0, split.overflowNode);
            }
            continue;
          }
        }
      } else if (node.type === 'table') {
        const split = splitTableNode(node, available, contentW, measurements);
        if (split?.fitNode) {
          const fitH = estimateHeight(split.fitNode, contentW, measurements);
          if (fitH <= available + 2) {
            currentChildren.push(split.fitNode);
            currentH += fitH;
            if (split.overflowNode) {
              flushPage();
              flatNodes.splice(i + 1, 0, split.overflowNode);
            }
            continue;
          }
        }
      }

      // Move node to next page (or force if page is empty)
      const hasContent = headerTpl
        ? currentChildren.length > 1
        : currentChildren.length > 0;
      if (hasContent) flushPage();
      currentChildren.push(node);
      currentH += nodeH;
    } else {
      currentChildren.push(node);
      currentH += nodeH;
    }
  }

  // Flush last page
  const hasContent = headerTpl
    ? currentChildren.length > 1
    : currentChildren.length > 0;

  // CRITICAL: If the document ends with a page-break, or is empty, or has content,
  // we must ensure the final page exists.
  const lastNode = flatNodes[flatNodes.length - 1];
  const endsWithPageBreak = lastNode?.type === 'page-break';

  if (hasContent || pages.length === 0 || endsWithPageBreak) {
    // Again, ensure content for the final potential trailing page
    const hasFocusableNode = currentChildren.some(
      (n: any) =>
        !['header-wrapper', 'footer-wrapper', 'page-break'].includes(n.type)
    );
    if (!hasFocusableNode) {
      currentChildren.push({type: 'paragraph', children: [{text: ''}]});
    }

    if (footerTpl) currentChildren.push(clone(footerTpl));

    pages.push(
      createPageLayout(pages.length + 1, currentChildren, pageDimensions.height)
    );
  }

  return pages;
};
